import Http from "./Http";
import { trackJobProgress, getStompClient } from "./socketService";
import {
  JOB_STATUS,
  JOB_STATUS_POLL_MS,
  JOB_STATUS_SAFETY_MS,
  JOB_TRACK_IDLE_MS,
  JOB_TIMED_OUT_MESSAGE,
  JOB_CARD_REMOVE_DELAY_MS,
  JOB_CARD_FAILED_REMOVE_DELAY_MS,
  COA_JOB_STATUS_URL,
} from "../constants/jobProgress";

const TERMINAL = new Set([JOB_STATUS.COMPLETED, JOB_STATUS.FAILED]);

export function isTerminal(data) {
  return TERMINAL.has(data?.status) || parseInt(data?.completedPercentage, 10) === 100;
}

// WebSocket first; status reads after subscribe, on reconnect, every pollMs while down / safetyMs while up.
// Finalizes once; gives up only after idleMs without a progress change.
export function trackJob({
  jobId,
  subscribe,
  isConnected,
  fetchStatus,
  onUpdate,
  onFinal,
  pollMs = JOB_STATUS_POLL_MS,
  safetyMs = JOB_STATUS_SAFETY_MS,
  idleMs = JOB_TRACK_IDLE_MS,
}) {
  let done = false;
  let timer = null;
  let inFlight = null;
  let dispose = () => {};
  let lastChangeAt = Date.now();
  let lastSignature = null;

  const finish = (data) => {
    if (done) return;
    done = true;
    if (timer) clearTimeout(timer);
    try {
      dispose();
    } catch (_) {}
    try {
      onFinal(data);
    } catch (err) {
      console.error("Job final handler failed", jobId, err);
    }
  };

  const apply = (data) => {
    if (done || !data) return;
    const signature = `${data.status}|${data.completedPercentage}|${data.message}`;
    if (signature !== lastSignature) {
      lastSignature = signature;
      lastChangeAt = Date.now();
    }
    onUpdate(data);
    if (isTerminal(data)) finish(data);
  };

  // At most one read in flight
  const readStatus = () => {
    if (done) return Promise.resolve();
    if (inFlight) return inFlight;
    inFlight = Promise.resolve()
      .then(() => fetchStatus(jobId))
      .then(apply, (err) => {
        // 403: stop so the global 403 toast doesn't repeat
        if (err?.response?.status === 403) finish({ status: JOB_STATUS.STOPPED });
      })
      .finally(() => {
        inFlight = null;
      });
    return inFlight;
  };

  const scheduleNext = () => {
    if (!done) timer = setTimeout(tick, isConnected() ? safetyMs : pollMs);
  };

  const tick = () => {
    timer = null;
    if (done) return;
    if (Date.now() - lastChangeAt >= idleMs) {
      finish({ status: JOB_STATUS.TIMED_OUT });
      return;
    }
    readStatus().then(scheduleNext);
  };

  dispose = subscribe(
    jobId,
    (msg) => {
      let data = null;
      try {
        data = JSON.parse(msg.body);
      } catch (_) {
        return;
      }
      apply(data);
    },
    readStatus
  );

  readStatus().then(scheduleNext);
  return { stop: () => finish({ status: JOB_STATUS.STOPPED }), isDone: () => done };
}

// Tracked card update: no-op keeps the same array, progress never goes back, frozen after a final state
export function applyTrackedUpdate(prevJobs, jobId, data) {
  const i = prevJobs.findIndex((j) => j.jobId === jobId);
  if (i === -1) return prevJobs;
  const job = prevJobs[i];
  if (TERMINAL.has(job.status)) return prevJobs;
  const status = data.status ?? job.status;
  const parsed = parseInt(data.completedPercentage, 10);
  const final = TERMINAL.has(status);
  const progress = Number.isNaN(parsed) ? job.progress : final ? parsed : Math.max(job.progress ?? 0, parsed);
  const message = data.message ?? job.message;
  if (status === job.status && progress === job.progress && message === job.message) return prevJobs;
  const next = prevJobs.slice();
  next[i] = { ...job, status, progress, message };
  return next;
}

export function trackCoaJob({ jobId, label, addJob, updateJob, removeJob, onCompleted, onFailed }) {
  addJob(jobId, label, [], { tracked: true });
  return trackJob({
    jobId,
    subscribe: trackJobProgress,
    isConnected: () => !!getStompClient()?.connected,
    fetchStatus: (id) => Http.GET(COA_JOB_STATUS_URL(id), {}, { _silent: true }).then((res) => res?.data ?? null),
    onUpdate: (data) => updateJob(jobId, data),
    onFinal: (data) => {
      if (data.status === JOB_STATUS.FAILED) {
        setTimeout(() => removeJob(jobId), JOB_CARD_FAILED_REMOVE_DELAY_MS);
        if (onFailed) onFailed(data);
      } else if (isTerminal(data)) {
        setTimeout(() => removeJob(jobId), JOB_CARD_REMOVE_DELAY_MS);
        if (onCompleted) onCompleted(data);
      } else if (data.status === JOB_STATUS.TIMED_OUT) {
        // Keep the card; tracking stopped, the job didn't fail
        updateJob(jobId, { message: JOB_TIMED_OUT_MESSAGE });
      } else {
        // STOPPED: no access, or cancelled by the caller
        removeJob(jobId);
      }
    },
  });
}
